import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadBlob } from './download';

/** Status payload of the backend export jobs (`/…/export/status/:jobId`). */
export interface JobStatus {
  jobId?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | (string & {});
  progress: number;
  processedFiles: number;
  totalFiles: number;
  failedFiles: number;
  message?: string;
  downloadUrl?: string;
  error?: string;
}

export interface JobRun {
  start: () => Promise<{ jobId: string }>;
  getStatus: (jobId: string) => Promise<JobStatus>;
  download: (jobId: string) => Promise<Blob>;
  fileName: string;
}

export type JobPhase = 'idle' | 'starting' | 'processing' | 'downloading' | 'done' | 'failed';

export interface JobState {
  phase: JobPhase;
  /** 0–100 */
  progress: number;
  processed: number;
  total: number;
  failed: number;
  error?: string;
  label?: string;
}

const IDLE: JobState = { phase: 'idle', progress: 0, processed: 0, total: 0, failed: 0 };
const POLL_MS = 2000; // production polling interval

/**
 * Start → poll → download flow for backend export jobs, as in the production export buttons.
 * `onDone` receives the final status (to report skipped files); errors land in `state.error`.
 */
export function useBackgroundJob(options?: {
  onDone?: (status: JobStatus) => void;
  onError?: (message: string) => void;
}) {
  const [state, setState] = useState<JobState>(IDLE);
  const timer = useRef<number | null>(null);
  const alive = useRef(true);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const run = useCallback(async (job: JobRun & { label?: string }) => {
    if (timer.current) window.clearTimeout(timer.current);
    setState({ ...IDLE, phase: 'starting', progress: 3, label: job.label });

    const fail = (error: unknown) => {
      if (!alive.current) return;
      const message = error instanceof Error ? error.message : String(error);
      setState((s) => ({ ...s, phase: 'failed', error: message }));
      optionsRef.current?.onError?.(message);
    };

    let jobId: string;
    try {
      ({ jobId } = await job.start());
    } catch (error) {
      fail(error);
      return;
    }

    const poll = async () => {
      if (!alive.current) return;
      try {
        const status = await job.getStatus(jobId);
        if (!alive.current) return;
        if (status.status === 'failed')
          throw new Error(status.error || status.message || 'Export failed');
        if (status.status === 'completed') {
          setState((s) => ({ ...s, phase: 'downloading', progress: 100 }));
          const blob = await job.download(jobId);
          downloadBlob(blob, job.fileName);
          if (!alive.current) return;
          setState((s) => ({
            ...s,
            phase: 'done',
            progress: 100,
            processed: status.processedFiles,
            total: status.totalFiles,
            failed: status.failedFiles,
          }));
          optionsRef.current?.onDone?.(status);
          return;
        }
        setState((s) => ({
          ...s,
          phase: 'processing',
          progress: Math.max(s.progress, Math.min(99, status.progress || 10)),
          processed: status.processedFiles + status.failedFiles,
          total: status.totalFiles,
          failed: status.failedFiles,
        }));
        timer.current = window.setTimeout(poll, POLL_MS);
      } catch (error) {
        fail(error);
      }
    };

    setState((s) => ({ ...s, phase: 'processing', progress: 10 }));
    await poll();
  }, []);

  const reset = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    setState(IDLE);
  }, []);

  const busy =
    state.phase === 'starting' || state.phase === 'processing' || state.phase === 'downloading';
  return { state, busy, run, reset };
}
